import React from 'react';
import { useLocation } from 'react-router';
import { Navigate, Outlet, Route, Routes as RouterRoutes } from 'react-router-dom';

import RetrieveQuote from '~/components/RetrieveQuote';
import { WithStepValidation } from '~/components/WithStepValidation';
import { AppLayout } from '~/components-ds/AppLayout';
import {
  BUSINESS_DETAILS_ROUTE,
  CONFIRMATION_ROUTE,
  COVER_DETAILS_BREAKDOWN_ROUTE,
  COVER_DETAILS_BUILDING_AND_CONTENTS_ROUTE,
  COVER_DETAILS_BUSINESS_INTERRUPTION_ROUTE,
  COVER_DETAILS_GLASS_ROUTE,
  COVER_DETAILS_LIABILITY_ROUTE,
  COVER_DETAILS_MONEY_ROUTE,
  COVER_DETAILS_PORTABLE_ITEMS_ROUTE,
  COVER_DETAILS_ROUTE,
  COVER_DETAILS_TAX_AUDIT_ROUTE,
  COVER_DETAILS_THEFT_ROUTE,
  EXTRA_INFO_CUSTOMER_HISTORY_ROUTE,
  EXTRA_INFO_MORE_BUSINESS_DETAILS_ROUTE,
  EXTRA_INFO_ROUTE,
  MEMBERSHIP_ROUTE,
  PAY_MY_BILL_CONFIRMATION_ROUTE,
  PAY_MY_BILL_ROUTE,
  PAYMENTS_ROUTE,
  QUOTE_ROUTE,
  RETRIEVE_QUOTE_ROUTE,
  REVIEW_ROUTE,
  SELECT_COVER_ROUTE,
  STAFF_ROUTE,
} from '~/constants/routes';
import PayMyBill from '~/pages/PayMyBill';
import PayMyBillConfirmation from '~/pages/PayMyBillConfirmation';
import Staff from '~/pages/Staff';
import BusinessDetails from '~/steps/BusinessDetails';
import Confirmation from '~/steps/Confirmation';
import CoverDetails from '~/steps/CoverDetails';
import CoverDetailsBreakdown from '~/steps/CoverDetailsBreakdown';
import CoverDetailsBuildingAndContents from '~/steps/CoverDetailsBuildingAndContents';
import CoverDetailsBusinessInterruption from '~/steps/CoverDetailsBusinessInterruption';
import CoverDetailsGlass from '~/steps/CoverDetailsGlass';
import BusinessLiability from '~/steps/CoverDetailsLiability';
import CoverDetailsMoney from '~/steps/CoverDetailsMoney';
import PortableItems from '~/steps/CoverDetailsPortableItems';
import CoverDetailsTaxAudit from '~/steps/CoverDetailsTaxAudit/CoverDetailsTaxAudit';
import CoverDetailsTheft from '~/steps/CoverDetailsTheft';
import ExtraInfo from '~/steps/ExtraInfo';
import ExtraInfoCustomerHistory from '~/steps/ExtraInfoCustomerHistory';
import ExtraInfoMoreBusinessDetails from '~/steps/ExtraInfoMoreBusinessDetails';
import Membership from '~/steps/Membership';
import Payments from '~/steps/Payments';
import Quote from '~/steps/Quote';
import Review from '~/steps/Review';
import SelectCover from '~/steps/SelectCover';
import useTrackRoute from '~/utils/analytics/useTrackRoute';

/** @description Removes the leading '/' from route constants */
const toPath = (route: string) => route.slice(1);

/**
 * @description Removes the first leading '/' and takes the last URL segment from route constants
 * i.e /cover-details/portable-items
 *     becomes
 *     /portable-items
 * */
const toNestedPath = (route: string) => route.split('/').slice(-1)[0];

const Routes: React.FC = () => {
  const { pathname, search } = useLocation();

  useTrackRoute(pathname);

  return (
    <RouterRoutes>
      <Route
        path="/"
        element={
          <AppLayout>
            <Outlet />
          </AppLayout>
        }
      >
        <Route path={toPath(RETRIEVE_QUOTE_ROUTE)} element={<RetrieveQuote />} />
        <Route index element={<Navigate to={BUSINESS_DETAILS_ROUTE + search} />} />
        <Route
          path={toPath(BUSINESS_DETAILS_ROUTE)}
          element={
            <WithStepValidation>
              <BusinessDetails />
            </WithStepValidation>
          }
        />
        <Route
          path={toPath(SELECT_COVER_ROUTE)}
          element={
            <WithStepValidation>
              <SelectCover />
            </WithStepValidation>
          }
        />
        <Route
          path={toPath(COVER_DETAILS_ROUTE)}
          element={
            <WithStepValidation>
              <CoverDetails />
            </WithStepValidation>
          }
        >
          <Route
            path={toNestedPath(COVER_DETAILS_LIABILITY_ROUTE)}
            element={
              <WithStepValidation>
                <BusinessLiability />
              </WithStepValidation>
            }
          />
          <Route
            path={toNestedPath(COVER_DETAILS_BUILDING_AND_CONTENTS_ROUTE)}
            element={
              <WithStepValidation>
                <CoverDetailsBuildingAndContents />
              </WithStepValidation>
            }
          />
          <Route
            path={toNestedPath(COVER_DETAILS_BUSINESS_INTERRUPTION_ROUTE)}
            element={
              <WithStepValidation>
                <CoverDetailsBusinessInterruption />
              </WithStepValidation>
            }
          />
          <Route
            path={toNestedPath(COVER_DETAILS_PORTABLE_ITEMS_ROUTE)}
            element={
              <WithStepValidation>
                <PortableItems />
              </WithStepValidation>
            }
          />
          <Route
            path={toNestedPath(COVER_DETAILS_THEFT_ROUTE)}
            element={
              <WithStepValidation>
                <CoverDetailsTheft />
              </WithStepValidation>
            }
          />
          <Route
            path={toNestedPath(COVER_DETAILS_MONEY_ROUTE)}
            element={
              <WithStepValidation>
                <CoverDetailsMoney />
              </WithStepValidation>
            }
          />
          <Route
            path={toNestedPath(COVER_DETAILS_GLASS_ROUTE)}
            element={
              <WithStepValidation>
                <CoverDetailsGlass />
              </WithStepValidation>
            }
          />
          <Route
            path={toNestedPath(COVER_DETAILS_BREAKDOWN_ROUTE)}
            element={
              <WithStepValidation>
                <CoverDetailsBreakdown />
              </WithStepValidation>
            }
          />
          <Route
            path={toNestedPath(COVER_DETAILS_TAX_AUDIT_ROUTE)}
            element={
              <WithStepValidation>
                <CoverDetailsTaxAudit />
              </WithStepValidation>
            }
          />
        </Route>
        <Route
          path={toPath(MEMBERSHIP_ROUTE)}
          element={
            <WithStepValidation>
              <Membership />
            </WithStepValidation>
          }
        />
        <Route
          path={toPath(QUOTE_ROUTE)}
          element={
            <WithStepValidation>
              <Quote />
            </WithStepValidation>
          }
        />
        <Route
          path={toPath(EXTRA_INFO_ROUTE)}
          element={
            <WithStepValidation>
              <ExtraInfo />
            </WithStepValidation>
          }
        >
          <Route
            path={toNestedPath(EXTRA_INFO_CUSTOMER_HISTORY_ROUTE)}
            element={
              <WithStepValidation>
                <ExtraInfoCustomerHistory />
              </WithStepValidation>
            }
          />
          <Route
            path={toNestedPath(EXTRA_INFO_MORE_BUSINESS_DETAILS_ROUTE)}
            element={
              <WithStepValidation>
                <ExtraInfoMoreBusinessDetails />
              </WithStepValidation>
            }
          />
        </Route>
        <Route
          path={toPath(REVIEW_ROUTE)}
          element={
            <WithStepValidation>
              <Review />
            </WithStepValidation>
          }
        />
        <Route
          path={toPath(PAYMENTS_ROUTE)}
          element={
            <WithStepValidation>
              <Payments />
            </WithStepValidation>
          }
        />
        <Route
          path={toPath(CONFIRMATION_ROUTE)}
          element={
            <WithStepValidation>
              <Confirmation />
            </WithStepValidation>
          }
        />
        <Route path="*" element={<Navigate to={BUSINESS_DETAILS_ROUTE + search} />} />
      </Route>

      <Route
        path={toPath(PAY_MY_BILL_ROUTE)}
        element={
          <AppLayout showStepper={false}>
            <PayMyBill />
          </AppLayout>
        }
      />
      <Route
        path={toPath(PAY_MY_BILL_CONFIRMATION_ROUTE)}
        element={
          <AppLayout showStepper={false}>
            <PayMyBillConfirmation />
          </AppLayout>
        }
      />
      <Route
        path={STAFF_ROUTE}
        element={
          <AppLayout showStepper={false}>
            <Staff />
          </AppLayout>
        }
      />
    </RouterRoutes>
  );
};

export default Routes;
